/**
 * Unit 2.4 · IMPL.B: the client workspace header (signpost, not dashboard).
 *
 * Bead: insurescope-11zkc.3.4.3.2. Design contract:
 * docs/design-briefs/2026-06-03-client-detail.md. Meta segments are JOINED
 * (never concatenated) so omitted segments leave no dangling interpuncts.
 * The action buttons render enabled no-ops until the Unit 3.x wiring lands
 * (combined release); callbacks arrive via props.
 */
import { formatDistanceToNowStrict } from "date-fns";
import {
    ArrowLeft,
    Calendar,
    Clock,
    FileSearch2,
    FileText,
    MessageSquare,
    Upload,
} from "lucide-react";
import { useScopesEnabled } from "@/contexts/frontend/organization/useScopesEnabled";
import type { ReactNode } from "react";
import { Link } from "react-router";

import type { IClientDetail } from "@/api";
import { Button } from "@/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatDate } from "@/utils/formatDate";

import { pluralCount } from "../utils/pluralCount";

import { IndustryChip } from "./IndustryChip";

export interface ClientDetailHeaderProps {
    client?: IClientDetail;
    isLoading?: boolean;
    onUploadDocument?: () => void;
    onNewScope?: () => void;
    onAskPolly?: () => void;
}

function MetaSegment({
    icon,
    children,
}: {
    icon: ReactNode;
    children: ReactNode;
}) {
    return (
        <span className="flex items-center gap-1.5">
            {icon}
            {children}
        </span>
    );
}

export function ClientDetailHeader({
    client,
    isLoading = false,
    onUploadDocument,
    onNewScope,
    onAskPolly,
}: ClientDetailHeaderProps) {
    const scopesEnabled = useScopesEnabled();
    const segments: ReactNode[] = [];
    if (client?.last_activity) {
        segments.push(
            <MetaSegment
                key="activity"
                icon={<Clock className="size-3.5 shrink-0" />}
            >
                Last activity{" "}
                {formatDistanceToNowStrict(new Date(client.last_activity), {
                    addSuffix: true,
                })}
            </MetaSegment>
        );
    }
    if (client && client.page_count > 0) {
        segments.push(
            <MetaSegment
                key="pages"
                icon={<FileText className="size-3.5 shrink-0" />}
            >
                {pluralCount(client.page_count, "page")} on file
            </MetaSegment>
        );
    }
    if (client) {
        segments.push(
            <MetaSegment
                key="since"
                icon={<Calendar className="size-3.5 shrink-0" />}
            >
                Client since {formatDate(client.created_at, "MMM d, yyyy")}
            </MetaSegment>
        );
    }

    return (
        <div className="bg-muted/30 border-b p-4">
            <Link
                to="/organizations/clients"
                className="text-muted-foreground hover:text-foreground mb-3 flex w-fit items-center gap-1 text-sm transition-colors"
            >
                <ArrowLeft className="size-3.5" />
                All clients
            </Link>

            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex min-w-0 flex-col gap-2">
                    {isLoading || !client ? (
                        <>
                            <span className="bg-muted h-7 w-64 animate-pulse rounded" />
                            <span className="bg-muted h-5 w-24 animate-pulse rounded" />
                            <span className="bg-muted h-4 w-80 animate-pulse rounded" />
                        </>
                    ) : (
                        <>
                            <Tooltip>
                                <TooltipTrigger
                                    render={
                                        <h1 className="text-primary max-w-xl truncate text-2xl font-bold tracking-tight" />
                                    }
                                >
                                    {client.name}
                                </TooltipTrigger>
                                <TooltipContent>{client.name}</TooltipContent>
                            </Tooltip>
                            {client.legal_name && (
                                <p
                                    className="text-muted-foreground max-w-xl truncate text-sm"
                                    title={client.legal_name}
                                >
                                    {client.legal_name}
                                </p>
                            )}
                            {client.industries.length > 0 && (
                                <span className="flex flex-wrap items-center gap-1">
                                    {client.industries.map((industry) => (
                                        <IndustryChip
                                            key={industry.id}
                                            name={industry.name}
                                        />
                                    ))}
                                </span>
                            )}
                            <span className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                                {segments}
                            </span>
                        </>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        variant="outline"
                        disabled={isLoading || !client}
                        onClick={onUploadDocument}
                    >
                        <Upload className="size-4" />
                        Upload document
                    </Button>
                    {scopesEnabled && (
                        <Button
                            variant="outline"
                            disabled={isLoading || !client}
                            onClick={onNewScope}
                        >
                            <FileSearch2 className="size-4" />
                            New scope
                        </Button>
                    )}
                    {/* The page's ONLY copper: the brand's "act now" verb. */}
                    <Button
                        className="bg-brand-copper text-brand-copper-foreground hover:bg-brand-copper/90"
                        disabled={isLoading || !client}
                        onClick={onAskPolly}
                    >
                        <MessageSquare className="size-4" />
                        Ask Polly
                    </Button>
                </div>
            </div>
        </div>
    );
}
